import React, { useState } from 'react';
import { ScrollText, Loader2, Download } from 'lucide-react';

// Quick admin audit-trail peek for the Developer Portal. Reads
// GET /api/mgmt/v1/admin/audit (audit:read). Uses the pasted mgmt key
// when present, otherwise the signed-in admin's session cookie.
// The "Embed origin changes" chip lets security spot origin edits fast.
const FILTERS = [
  { key: 'all', label: 'All', action: '' },
  { key: 'origins', label: 'Embed origin changes', action: 'embed.origins.update' },
];

function actionColor(a) {
  if (a === 'embed.origins.update') return 'text-emerald-300';
  if (a?.includes('delete') || a?.includes('revoke')) return 'text-rose-300';
  return 'text-sky-300';
}

// Added / removed origins for an embed.origins.update entry.
function originDiff(meta) {
  const oldSet = new Set(meta?.old || []);
  const newSet = new Set(meta?.new || []);
  const added = (meta?.new || []).filter((o) => !oldSet.has(o));
  const removed = (meta?.old || []).filter((o) => !newSet.has(o));
  return { added, removed };
}

function toCsv(rows) {
  const cols = ['ts', 'action', 'resource_type', 'resource_id', 'actor_type', 'actor_id', 'act_as_sub', 'ip', 'origins_old', 'origins_new'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const cell = (r, c) => {
    if (c === 'origins_old') return (r.metadata?.old || []).join(' ');
    if (c === 'origins_new') return (r.metadata?.new || []).join(' ');
    return r[c];
  };
  const lines = [cols.join(',')];
  rows.forEach((r) => lines.push(cols.map((c) => esc(cell(r, c))).join(',')));
  return lines.join('\n');
}

export default function AuditTrailViewer({ apiBase, bearerKey }) {
  const [expanded, setExpanded] = useState(false);
  const [active, setActive] = useState('origins');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [actor, setActor] = useState('');

  const load = async (filterKey, opts = {}) => {
    const f = FILTERS.find((x) => x.key === filterKey) || FILTERS[0];
    const key = bearerKey?.trim();
    const from = opts.from !== undefined ? opts.from : fromDate;
    const to = opts.to !== undefined ? opts.to : toDate;
    const who = opts.actor !== undefined ? opts.actor : actor;
    setLoading(true); setErr(''); setRows(null);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (f.action) params.set('action', f.action);
      if (from) params.set('since', `${from}T00:00:00.000Z`);
      if (to) params.set('until', `${to}T23:59:59.999Z`);
      if (who && who.trim()) params.set('actor_id', who.trim());
      const res = await fetch(`${apiBase}/api/mgmt/v1/admin/audit?${params.toString()}`, {
        headers: key ? { Authorization: `Bearer ${key}` } : {},
        credentials: key ? 'same-origin' : 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setRows(data.data || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally { setLoading(false); }
  };

  const applyPreset = (days) => {
    const to = new Date();
    const from = new Date(Date.now() - days * 86400000);
    const fromStr = from.toISOString().slice(0, 10);
    const toStr = to.toISOString().slice(0, 10);
    setFromDate(fromStr); setToDate(toStr);
    load(active, { from: fromStr, to: toStr });
  };

  const filterByActor = (id) => {
    if (!id) return;
    setActor(id);
    load(active, { actor: id });
  };

  // ── Saved views ────────────────────────────────────────────────────────
  const [views, setViews] = useState([]);
  const [viewName, setViewName] = useState('');

  const loadViews = async () => {
    const key = bearerKey?.trim();
    try {
      const res = await fetch(`${apiBase}/api/mgmt/v1/admin/audit/views`, {
        headers: key ? { Authorization: `Bearer ${key}` } : {},
        credentials: key ? 'same-origin' : 'include',
      });
      const data = await res.json();
      if (res.ok) setViews(data.data || []);
    } catch { /* non-fatal */ }
  };

  const saveView = async () => {
    const name = viewName.trim();
    if (!name) return;
    const f = FILTERS.find((x) => x.key === active) || FILTERS[0];
    const key = bearerKey?.trim();
    const filters = { action: f.action, actor_id: actor.trim(), since: fromDate, until: toDate };
    try {
      const res = await fetch(`${apiBase}/api/mgmt/v1/admin/audit/views`, {
        method: 'POST',
        headers: { ...(key ? { Authorization: `Bearer ${key}` } : {}), 'Content-Type': 'application/json' },
        credentials: key ? 'same-origin' : 'include',
        body: JSON.stringify({ name, filters }),
      });
      if (res.ok) { setViewName(''); loadViews(); }
    } catch { /* ignore */ }
  };

  const deleteView = async (id) => {
    const key = bearerKey?.trim();
    try {
      await fetch(`${apiBase}/api/mgmt/v1/admin/audit/views/${id}`, {
        method: 'DELETE',
        headers: key ? { Authorization: `Bearer ${key}` } : {},
        credentials: key ? 'same-origin' : 'include',
      });
      loadViews();
    } catch { /* ignore */ }
  };

  const applyView = (v) => {
    const f = v.filters || {};
    const fKey = (FILTERS.find((x) => x.action === (f.action || '')) || FILTERS[0]).key;
    setActive(fKey);
    setActor(f.actor_id || '');
    setFromDate(f.since || '');
    setToDate(f.until || '');
    load(fKey, { actor: f.actor_id || '', from: f.since || '', to: f.until || '' });
  };

  const pick = (k) => { setActive(k); load(k); };

  const exportCsv = () => {
    if (!rows || rows.length === 0) return;
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inzara_audit_${active}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border border-border/40 bg-muted/10 mb-6" data-testid="audit-trail-viewer">
      <button
        type="button"
        onClick={() => { const next = !expanded; setExpanded(next); if (next && rows === null) { load(active); loadViews(); } }}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid="audit-trail-toggle"
      >
        <div className="flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Audit trail</span>
          <span className="text-[10px] text-muted-foreground">who changed what, and when</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/40 pt-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => pick(f.key)}
                className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                  active === f.key
                    ? 'border-primary/60 bg-primary/15 text-primary'
                    : 'border-border/40 bg-background/40 text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`audit-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            <button
              type="button"
              onClick={exportCsv}
              disabled={!rows || rows.length === 0}
              className="ml-auto flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-border/40 bg-background/40 text-muted-foreground hover:text-foreground disabled:opacity-40"
              data-testid="audit-export-csv"
            >
              <Download className="w-3 h-3" /> Export CSV
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap" data-testid="audit-date-range">
            <span className="text-[10px] text-muted-foreground">Quick:</span>
            <button type="button" onClick={() => applyPreset(1)} className="text-[10px] px-2 py-1 rounded-full border border-border/40 bg-background/40 text-muted-foreground hover:text-foreground" data-testid="audit-preset-24h">Last 24h</button>
            <button type="button" onClick={() => applyPreset(7)} className="text-[10px] px-2 py-1 rounded-full border border-border/40 bg-background/40 text-muted-foreground hover:text-foreground" data-testid="audit-preset-7d">Last 7d</button>
            <button type="button" onClick={() => applyPreset(30)} className="text-[10px] px-2 py-1 rounded-full border border-border/40 bg-background/40 text-muted-foreground hover:text-foreground" data-testid="audit-preset-30d">Last 30d</button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[10px] text-muted-foreground">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="text-[10px] px-2 py-1 rounded border border-border/40 bg-background/60"
              data-testid="audit-from-date"
            />
            <label className="text-[10px] text-muted-foreground">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="text-[10px] px-2 py-1 rounded border border-border/40 bg-background/60"
              data-testid="audit-to-date"
            />
            <button
              type="button"
              onClick={() => load(active)}
              className="text-[10px] px-2 py-1 rounded border border-primary/50 bg-primary/10 text-primary"
              data-testid="audit-apply-range"
            >
              Apply
            </button>
            {(fromDate || toDate) && (
              <button
                type="button"
                onClick={() => { setFromDate(''); setToDate(''); load(active, { from: '', to: '' }); }}
                className="text-[10px] px-2 py-1 rounded text-muted-foreground hover:text-foreground"
                data-testid="audit-clear-range"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap" data-testid="audit-actor-filter">
            <label className="text-[10px] text-muted-foreground">Actor</label>
            <input
              value={actor}
              onChange={(e) => setActor(e.target.value)}
              placeholder="who made the change (actor id / key id)"
              spellCheck={false}
              className="flex-1 min-w-[180px] font-mono text-[10px] px-2 py-1 rounded border border-border/40 bg-background/60"
              data-testid="audit-actor-input"
            />
            <button
              type="button"
              onClick={() => load(active)}
              className="text-[10px] px-2 py-1 rounded border border-primary/50 bg-primary/10 text-primary"
              data-testid="audit-actor-apply"
            >
              Filter
            </button>
            {actor && (
              <button
                type="button"
                onClick={() => { setActor(''); load(active, { actor: '' }); }}
                className="text-[10px] px-2 py-1 rounded text-muted-foreground hover:text-foreground"
                data-testid="audit-actor-clear"
              >
                Clear
              </button>
            )}
          </div>

          {/* Saved views — favourite filter combos */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="audit-saved-views">
            <input
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              placeholder="name this filter…"
              className="text-[10px] px-2 py-1 rounded border border-border/40 bg-background/60"
              data-testid="audit-view-name"
            />
            <button
              type="button"
              onClick={saveView}
              disabled={!viewName.trim()}
              className="text-[10px] px-2 py-1 rounded border border-primary/50 bg-primary/10 text-primary disabled:opacity-40"
              data-testid="audit-view-save"
            >
              Save view
            </button>
            {views.map((v) => (
              <span key={v.view_id} className="inline-flex items-center gap-1 text-[10px] pl-2 pr-1 py-1 rounded-full border border-border/40 bg-background/40" data-testid="audit-view-chip">
                <button type="button" onClick={() => applyView(v)} className="hover:text-foreground text-muted-foreground" data-testid="audit-view-apply">{v.name}</button>
                <button type="button" onClick={() => deleteView(v.view_id)} className="text-muted-foreground hover:text-rose-400" data-testid="audit-view-delete" aria-label="delete view">×</button>
              </span>
            ))}
          </div>

          {!bearerKey?.trim() && (
            <p className="text-[10px] text-muted-foreground" data-testid="audit-trail-nokey">
              Uses your pasted API key, or your signed-in admin session if you&rsquo;re logged in.
            </p>
          )}
          {err && <p className="text-[11px] text-rose-400" data-testid="audit-trail-error">{err}</p>}

          {rows !== null && !err && (
            rows.length === 0 ? (
              <p className="text-[11px] text-muted-foreground" data-testid="audit-trail-empty">No audit entries for this filter.</p>
            ) : (
              <div className="rounded border border-border/40 divide-y divide-border/30" data-testid="audit-trail-list">
                {rows.map((r) => (
                  <div key={r.log_id} className="px-3 py-2 text-xs" data-testid="audit-trail-row">
                    <div className="flex items-center justify-between gap-2">
                      <code className={`font-mono text-[11px] font-semibold ${actionColor(r.action)}`}>{r.action}</code>
                      <span className="text-[10px] text-muted-foreground">{r.ts ? new Date(r.ts).toLocaleString() : ''}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {r.resource_type}{r.resource_id ? ` · ${r.resource_id}` : ''}
                      {' · by '}
                      <button
                        type="button"
                        onClick={() => filterByActor(r.actor_id || '')}
                        className="underline decoration-dotted hover:text-foreground"
                        data-testid="audit-row-actor"
                      >
                        {r.actor_id || 'system'}
                      </button>
                      {r.act_as_sub ? ` (as ${r.act_as_sub})` : ''}
                    </div>
                    {r.action === 'embed.origins.update' && r.metadata && (() => {
                      const { added, removed } = originDiff(r.metadata);
                      return (
                        <div className="mt-1 space-y-0.5" data-testid="audit-origin-diff">
                          {added.map((o) => (
                            <div key={`a-${o}`} className="text-[10px] font-mono text-emerald-300/90">+ {o}</div>
                          ))}
                          {removed.map((o) => (
                            <div key={`r-${o}`} className="text-[10px] font-mono text-rose-300/90">− {o}</div>
                          ))}
                          {added.length === 0 && removed.length === 0 && (
                            <div className="text-[10px] font-mono text-muted-foreground">no net change</div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}
